import { Command } from 'cmdk';
import { Check, ChevronDown, Search } from 'lucide-react';
import { Popover } from 'radix-ui';
import { type ReactNode, useState } from 'react';
import { cn } from '../lib/cn';
import { commandFilter } from '../lib/search';
import { useFieldControl } from './Field';
import { controlClasses, controlSizes } from './Input';

export interface ComboboxOption {
  value: string;
  label: string;
  /** Dodatkowy opis (np. klient przy projekcie). */
  description?: string;
  /** Np. kropka koloru klienta. */
  leading?: ReactNode;
  keywords?: string[];
}

/** Wybór z wyszukiwaniem (Popover + cmdk). Wyszukiwanie ignoruje polskie znaki. */
export function Combobox({
  value,
  onValueChange,
  options,
  placeholder = 'Wybierz…',
  searchPlaceholder = 'Szukaj…',
  emptyText = 'Brak wyników',
  size = 'm',
  disabled,
  id,
  className,
  'aria-label': ariaLabel,
}: {
  value: string | null;
  onValueChange: (value: string) => void;
  options: ComboboxOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  size?: keyof typeof controlSizes;
  disabled?: boolean;
  id?: string;
  className?: string;
  'aria-label'?: string;
}) {
  const [open, setOpen] = useState(false);
  const field = useFieldControl({ id });
  const selected = options.find((o) => o.value === value);

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger
        id={field.id}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-describedby={field['aria-describedby']}
        aria-invalid={field['aria-invalid']}
        role="combobox"
        aria-expanded={open}
        className={cn(
          controlClasses,
          controlSizes[size],
          'inline-flex items-center justify-between gap-2 text-left',
          !selected && 'text-fg-muted',
          className,
        )}
      >
        <span className="flex min-w-0 items-center gap-2">
          {selected?.leading}
          <span className="truncate">{selected?.label ?? placeholder}</span>
        </span>
        <ChevronDown size={16} strokeWidth={1.75} aria-hidden className="shrink-0 text-fg-muted" />
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={4}
          className="z-50 w-[max(var(--radix-popover-trigger-width),240px)] rounded-lg border border-line bg-canvas shadow-popover animate-pop-in"
        >
          <Command filter={commandFilter} loop>
            <div className="flex items-center gap-2 border-b border-line px-3">
              <Search size={16} strokeWidth={1.75} aria-hidden className="text-fg-muted" />
              <Command.Input
                placeholder={searchPlaceholder}
                className="h-10 w-full bg-transparent text-body-s text-fg outline-none placeholder:text-fg-muted"
              />
            </div>
            <Command.List className="max-h-64 overflow-y-auto p-1">
              <Command.Empty className="px-2.5 py-3 text-body-s text-fg-muted">{emptyText}</Command.Empty>
              {options.map((option) => (
                <Command.Item
                  key={option.value}
                  value={`${option.label} ${option.value}`}
                  keywords={[option.description ?? '', ...(option.keywords ?? [])]}
                  onSelect={() => {
                    onValueChange(option.value);
                    setOpen(false);
                  }}
                  className="flex h-8 cursor-pointer items-center gap-2 rounded-md px-2.5 text-body-s text-fg data-[selected=true]:bg-elevated"
                >
                  {option.leading}
                  <span className="truncate">{option.label}</span>
                  {option.description && <span className="truncate text-fg-muted">{option.description}</span>}
                  <span className="flex-1" />
                  {option.value === value && <Check size={14} strokeWidth={2} aria-hidden className="text-accent" />}
                </Command.Item>
              ))}
            </Command.List>
          </Command>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
